import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Form,
    Grid,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import {
    DollarOutlined,
    FileTextOutlined,
    InfoCircleOutlined,
    UserOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import {
    fetchCommissionDashboardItem,
    requestCommissionAdjustment,
} from '@/redux/actions/commissionActions';

import { checkPermission } from '@/utils/permission';
import {
    formatCommissionMoney,
    commissionStatus,
} from './commissionDashboardHelpers';

const { Text, Title } = Typography;

export default function CommissionDashboardDetailDrawer({
    itemId,
    visible,
    onClose,
}) {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [adjustmentVisible, setAdjustmentVisible] = useState(false);

    const [form] = Form.useForm();

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const canRequestAdjustment = checkPermission(
        'commission_adjustment',
        'request'
    );

    const storedDetail = useSelector(
        state => state.commissionReducer.dashboardDetail
    );

    const detail =
        String(storedDetail?.item?.id) === String(itemId)
            ? storedDetail
            : null;

    useEffect(() => {
        if (!visible || !itemId) return;

        setLoading(true);

        dispatch(fetchCommissionDashboardItem(itemId))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [dispatch, visible, itemId]);

    const item = detail?.item;

    const status = commissionStatus(item?.status);

    const receipts =
        detail?.source?.source_portions ||
        detail?.source?.receipt_allocations ||
        [];

    const condition = detail?.source?.condition;

    const submitAdjustment = async () => {
        const values = await form.validateFields();

        await dispatch(
            requestCommissionAdjustment(item.id, {
                ...values,
                adjustment_period_key:
                    values.adjustment_period_key?.format('YYYY-MM'),
            })
        );

        setAdjustmentVisible(false);
        form.resetFields();
    };

    const moneyItems = [
        {
            label: 'Doanh thu tính: ',
            value: item?.base_revenue,
        },
        {
            label: 'Hoa hồng phát sinh: ',
            value: item?.gross_amount,
        },
        {
            label: 'Giữ lại: ',
            value: item?.hold_amount,
        },
        {
            label: 'Điều chỉnh: ',
            value: item?.adjustment_amount,
        },
    ];

    return (
        <>
            <Drawer
                visible={visible}
                width={isMobile ? '100%' : 800}
                title="Chi tiết khoản hoa hồng"
                onClose={onClose}
                destroyOnClose
                bodyStyle={{
                    padding: isMobile ? 12 : 20,
                    background: '#f5f7fa',
                    overflowX: 'hidden',
                }}
                footer={
                    item && canRequestAdjustment ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                type="primary"
                                onClick={() => setAdjustmentVisible(true)}
                            >
                                Yêu cầu điều chỉnh
                            </Button>
                        </div>
                    ) : null
                }
            >
                <Spin spinning={loading}>
                    {!item ? (
                        <Card bordered={false}>
                            <Empty description="Chưa có dữ liệu" />
                        </Card>
                    ) : (
                        <Space
                            direction="vertical"
                            size={16}
                            style={{ width: '100%' }}
                        >
                            {/* Tổng quan */}
                            <Card bordered={false}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 16,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <div>
                                        <Text
                                            type="secondary"
                                            style={{
                                                display: 'block',
                                                marginBottom: 4,
                                            }}
                                        >
                                            Khoản hoa hồng
                                        </Text>

                                        <Title
                                            level={3}
                                            style={{
                                                margin: 0,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {formatCommissionMoney(
                                                item.net_amount
                                            )}
                                        </Title>

                                        <Text type="secondary">
                                            Kỳ{' '}
                                            {item.period_key
                                                ? moment(
                                                    item.period_key,
                                                    'YYYY-MM'
                                                ).format('MM/YYYY')
                                                : '—'}
                                        </Text>
                                    </div>

                                    <Tag
                                        color={status.color}
                                        style={{
                                            margin: 0,
                                            padding: '4px 10px',
                                            fontSize: 13,
                                        }}
                                    >
                                        {status.label}
                                    </Tag>
                                </div>

                                <Divider style={{ margin: '16px 0' }} />

                                <Row gutter={[16, 12]}>
                                    <Col xs={24} md={12}>
                                        <Space align="start">
                                            <UserOutlined
                                                style={{
                                                    marginTop: 4,
                                                    color: '#8c8c8c',
                                                }}
                                            />

                                            <div>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        display: 'block',
                                                    }}
                                                >
                                                    Nhân sự
                                                </Text>

                                                <Text strong>
                                                    {item.employee?.full_name ||
                                                        '—'}
                                                </Text>

                                                {item.role_label && (
                                                    <Text
                                                        type="secondary"
                                                        style={{
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {item.role_label}
                                                    </Text>
                                                )}
                                            </div>
                                        </Space>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Space align="start">
                                            <InfoCircleOutlined
                                                style={{
                                                    marginTop: 4,
                                                    color: '#8c8c8c',
                                                }}
                                            />

                                            <div>
                                                <Text type="secondary">
                                                    Khách hàng {' '}
                                                </Text>
                                                <Text strong>
                                                    {[
                                                        item.customer?.code,
                                                        item.customer?.full_name,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' · ') || '—'}
                                                </Text>
                                            </div>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Giá trị hoa hồng */}
                            <Card
                                bordered={false}
                                title={
                                    <Space>
                                        <DollarOutlined />
                                        <span>Giá trị hoa hồng</span>
                                    </Space>
                                }
                            >
                                <Row gutter={[12, 8]}>
                                    {moneyItems.map(row => (
                                        <Col
                                            xs={12}
                                            md={6}
                                            key={row.label}
                                        >
                                            <div
                                                style={{
                                                    minHeight: 64,
                                                    padding: '8px 14px',
                                                    borderRadius: 6,
                                                    background: '#fafafa',
                                                    border:
                                                        '1px solid #f0f0f0',
                                                }}
                                            >
                                                <Text type="secondary">
                                                    {row.label}
                                                </Text>

                                                <Text
                                                    strong
                                                    style={{ fontSize: 15 }}
                                                >
                                                    {formatCommissionMoney(
                                                        row.value
                                                    )}
                                                </Text>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>

                                <div
                                    style={{
                                        marginTop: 8,
                                        padding: '10px 16px',
                                        borderRadius: 6,
                                        background: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Text strong>Còn lại</Text>

                                    <Text
                                        strong
                                        style={{
                                            fontSize: 20,
                                        }}
                                    >
                                        {formatCommissionMoney(
                                            item.net_amount
                                        )}
                                    </Text>
                                </div>
                            </Card>

                            {/* Thông tin dịch vụ */}
                            <Card
                                bordered={false}
                                title="Thông tin liên quan"
                            >
                                <Descriptions
                                    size="small"
                                    column={isMobile ? 1 : 2}
                                    colon={false}
                                >
                                    <Descriptions.Item label="Dịch vụ">
                                        {item.service?.name || '—'}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Nhóm dịch vụ">
                                        {item.service?.group_name || '—'}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Vai trò">
                                        {item.role_label || '—'}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Kỳ hoa hồng">
                                        {item.period_key
                                            ? moment(
                                                item.period_key,
                                                'YYYY-MM'
                                            ).format('MM/YYYY')
                                            : '—'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            {/* Nguồn hình thành */}
                            <Card
                                bordered={false}
                                title={
                                    <Space>
                                        <FileTextOutlined />
                                        <span>
                                            Nguồn hình thành khoản hoa hồng
                                        </span>
                                    </Space>
                                }
                            >
                                <Descriptions
                                    size="small"
                                    column={1}
                                    colon={false}
                                >
                                    <Descriptions.Item label="Nguồn phát sinh">
                                        {item.event_label || '—'}
                                    </Descriptions.Item>

                                    {condition && (
                                        <Descriptions.Item label="Căn cứ ghi nhận">
                                            {[
                                                condition.condition_name ||
                                                'Mốc nghiệp vụ đã hoàn thành',
                                                condition.occurred_at
                                                    ? moment(
                                                        condition.occurred_at
                                                    ).format('DD/MM/YYYY')
                                                    : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </Descriptions.Item>
                                    )}

                                    <Descriptions.Item label="Phiếu thu liên quan">
                                        {!receipts.length ? (
                                            <Text type="secondary">
                                                Không có phiếu thu liên quan
                                            </Text>
                                        ) : (
                                            <Space
                                                direction="vertical"
                                                size={4}
                                            >
                                                {receipts.map(
                                                    (row, index) => (
                                                        <div
                                                            key={
                                                                row.id ||
                                                                index
                                                            }
                                                        >
                                                            <Text>
                                                                {row.receipt_code ||
                                                                    row.receipt_number ||
                                                                    row.code ||
                                                                    (row.receipt_id
                                                                        ? `Phiếu thu #${row.receipt_id}`
                                                                        : null) ||
                                                                    `Phiếu thu ${index + 1
                                                                    }`}
                                                            </Text>

                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    marginLeft: 8,
                                                                }}
                                                            >
                                                                {formatCommissionMoney(
                                                                    row.recognized_amount ??
                                                                    row.allocated_amount
                                                                )}
                                                            </Text>
                                                        </div>
                                                    )
                                                )}
                                            </Space>
                                        )}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Space>
                    )}
                </Spin>
            </Drawer>

            <Modal
                visible={adjustmentVisible}
                width={isMobile ? '100%' : 520}
                style={
                    isMobile
                        ? {
                            top: 0,
                            maxWidth: '100vw',
                            paddingBottom: 0,
                        }
                        : undefined
                }
                bodyStyle={{
                    maxHeight: isMobile
                        ? 'calc(100vh - 110px)'
                        : '70vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: isMobile ? 12 : 20,
                }}
                title="Yêu cầu điều chỉnh hoa hồng"
                okText="Gửi yêu cầu"
                cancelText="Hủy"
                onCancel={() => {
                    setAdjustmentVisible(false);
                    form.resetFields();
                }}
                onOk={submitAdjustment}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="adjustment_type"
                        label="Hướng điều chỉnh"
                        rules={[
                            {
                                required: true,
                                message:
                                    'Vui lòng chọn hướng điều chỉnh.',
                            },
                        ]}
                    >
                        <Select
                            placeholder="Chọn hướng điều chỉnh"
                            options={[
                                {
                                    value: 'DCT',
                                    label: 'Bổ sung hoa hồng',
                                },
                                {
                                    value: 'DCG',
                                    label: 'Giảm hoa hồng',
                                },
                                {
                                    value: 'TTHH',
                                    label:
                                        'Thu hồi hoa hồng đã ghi nhận',
                                },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label="Số tiền điều chỉnh"
                        rules={[
                            {
                                required: true,
                                message: 'Nhập số tiền.',
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            style={{ width: '100%' }}
                            placeholder="Nhập số tiền"
                        />
                    </Form.Item>

                    <Form.Item
                        name="adjustment_period_key"
                        label="Kỳ áp dụng"
                        extra="Có thể để trống để hệ thống sử dụng kỳ tại thời điểm yêu cầu được duyệt."
                    >
                        <DatePicker
                            picker="month"
                            format="MM/YYYY"
                            placeholder="Chọn kỳ áp dụng"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="Lý do và căn cứ điều chỉnh"
                        rules={[
                            {
                                required: true,
                                message:
                                    'Vui lòng nhập lý do điều chỉnh.',
                            },
                        ]}
                    >
                        <Input.TextArea
                            rows={4}
                            maxLength={1000}
                            showCount
                            placeholder="Mô tả lý do và căn cứ liên quan..."
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
